<script lang="ts">
    import PortfoliosPage from '@Quest/CorporateScenarios/Pages/PortfoliosPage'
    export default PortfoliosPage
</script>

<template>
    <div>
        <SelectorDialog :viewModel="selectorDialog" />

        <NumericInputDialog :viewModel="totalCostLimitEditorDialog" @update-value="handleSetPortfolioTotalCostLimitCommand" />
        <NumericInputDialog :viewModel="opexLimitEditorDialog" @update-value="handleSetPortfolioOpexLimitCommand" />
        <NumericInputDialog :viewModel="othexLimitEditorDialog" @update-value="handleSetPortfolioOthexLimitCommand" />
        <NumericInputDialog :viewModel="capexLimitEditorDialog" @update-value="handleSetPortfolioCapexLimitCommand" />
        <NumericInputDialog :viewModel="expensesLimitEditorDialog" @update-value="handleSetPortfolioExpensesLimitCommand" />
        <NumericInputDialog :viewModel="opexExpensesLimitEditorDialog" @update-value="handleSetPortfolioOpexExpensesLimitCommand" />

        <CorporateNavigation ActivePage="dashboard" />

        <div v-if="rm" class="container-fluid" style="margin-top: 80px; padding-top: 20px;">
            <div class="row">
                <div class="col-lg-6">
                    <small class="text-muted">Enterprise Plan: {{rm.ScenarioName}}</small>
                    <h1 style="font-size: 2em; font-weight: 300; color: #33475b; margin: 0;">Portfolios</h1>
                </div>
                <div class="col-lg-6 d-flex flex-column justify-content-end">
                    <ScenarioNavigation />
                </div>
            </div>

            <hr />

            <div class="mt-5">
                <table class="table table-bordered table-hover">
                    <thead class="table-secondary">
                        <tr>
                            <th colspan="2" class="align-middle">Portfolio</th>
                            <th class="align-middle text-center">Requested projects</th>
                            <th class="align-middle text-center">Selected projects</th>
                            <th class="align-middle text-center">Value points</th>
                            <th class="align-middle text-center">Revenue <br /> ({{rm.CurrencyLabel}})</th>
                            <th class="align-middle text-center" :title="rm.TotalCostLabel">Cost <br /> ({{rm.CurrencyLabel}})</th>
                            <th class="align-middle text-center">Cost of value point<br /> ({{rm.CurrencyLabel}})</th>
                            <th class="align-middle text-center">Strategic fit <br /> (0-100)</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-for="p in rm.Portfolios" :key="p.PortfolioId">

                            <td>
                                {{p.PortfolioName}}<br />
                                <template v-if="p.ScenarioId">
                                    <a class="text-primary" v-sanitizedHref="`/#/portfolio-plan/projects/${p.WorkspaceId}/${p.TchProjectId}/${p.ScenarioId}`">
                                        {{p.ScenarioName}}
                                    </a>
                                </template>
                                <template v-if="!p.ScenarioId">
                                    <a class="text-danger" v-sanitizedHref="`/#/portfolio-plans/${p.WorkspaceId}/${p.TchProjectId}`">
                                        No portfolio plan
                                    </a>
                                </template>
                            </td>
                            <td style="width: 120px;" class="text-center align-middle">
                                <div class="dropdown d-inline">
                                    <button type="button" class="btn btn-outline-secondary" data-toggle="dropdown">
                                        <i class="fa-solid fa-ellipsis"></i>
                                    </button>
                                    <div class="dropdown-menu dropdown-menu-right">
                                        <h6 class="dropdown-header">Portfolio plans</h6>
                                        <a class="dropdown-item" v-for="s in p.Scenarios" :key="s.Id" @click="handleUpdateActivePortfolioScenarioCommand(p.PortfolioId, s.Id)">{{s.Name}}</a>
                                        <div class="dropdown-divider"></div>
                                        <a class="dropdown-item" v-sanitizedHref="`/#/portfolio-plans/${p.WorkspaceId}/${p.TchProjectId}`">Manage plans</a>
                                    </div>
                                </div>
                                <button class="btn btn-success" @click="showSelectorDialog(p)" :disabled="p.ScenarioId == null" title="Selector">
                                    <i class="fa-solid fa-robot"></i>
                                </button>
                            </td>

                            <td class="text-center">
                                {{p.RequestedProjectsCount}}<br />
                                <small class="text-muted">{{fmtPct(p.RequestedProjectsCountShare)}}</small>
                            </td>
                            <td class="text-center">
                                {{p.AcceptedProjectsCount}}<br />
                                <small class="text-muted">{{fmtPct(p.AcceptedProjectsCountShare)}}</small>
                            </td>
                            <td class="text-center">
                                {{fmtNum(p.PlannedValuePoints)}}<br />
                                <small class="text-muted">{{fmtPct(p.PlannedValuePointsShare)}}</small>
                            </td>
                            <td class="text-center">
                                {{fmtNum(p.PlannedRevenue)}}<br />
                                <small class="text-muted">{{fmtPct(p.PlannedRevenueShare)}}</small>
                            </td>
                            <td class="text-center">
                                {{fmtNum(p.PlannedCost)}}<br />
                                <small class="text-muted">{{fmtPct(p.PlannedCostShare)}}</small>
                            </td>
                            <td class="text-center align-middle">
                                {{fmtNum(p.CostOfValuePoint)}}
                            </td>
                            <td class="text-center align-middle">
                                {{fmtNum(p.StrategicFit, 10)}}
                            </td>

                        </tr>

                    </tbody>
                    <tfoot class="">
                        <tr>
                            <td colspan="2"></td>
                            <th class="text-center">
                                {{rm.RequestedProjectsCount}}
                            </th>
                            <th class="text-center">
                                {{rm.AcceptedProjectsCount}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(rm.PlannedValuePoints)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(rm.PlannedRevenue)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(rm.PlannedCost)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(rm.CostOfValuePoint)}}
                            </th>
                            <td class="text-center">
                            </td>

                        </tr>
                    </tfoot>

                </table>
            </div>

            <div class="mt-5">
                <table class="table table-bordered table-hover">
                    <thead>
                        <tr class="table-secondary">
                            <th colspan="2" class="align-middle">Budget allocation</th>
                            <th class="align-middle text-center">Allocation <br /> ({{rm.CurrencyLabel}})</th>
                            <th class="align-middle text-center">Limit<br /> ({{rm.CurrencyLabel}})</th>
                        </tr>
                    </thead>
                    <tbody class="totalCost">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="budget.TotalCostCollapsed = !budget.TotalCostCollapsed">
                                    <i :class="['fa', budget.TotalCostCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                Total cost<br />
                                <small class="text-muted">{{rm.TotalCostLabel}}</small>
                            </th>

                            <th class="text-center">
                                {{fmtNum(budget.TotalCostCurrentValue)}}
                            </th>

                            <th class="text-center">
                                {{fmtNum(budget.TotalCostLimit)}}
                            </th>

                        </tr>
                        <tr v-show="budget.TotalCostCollapsed === false" v-for="p in budget.TotalCostItems" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>

                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}
                            </td>

                            <td class="text-center">
                                <template v-if="p.Limit != null">
                                    <a class="text-primary" @click="showTotalCostLimitEditor(p)">{{fmtNum(p.Limit)}}</a>
                                </template>
                                <template v-if="p.Limit == null">
                                    <a class="text-danger font-italic pointer" @click="showTotalCostLimitEditor(p)">Not set</a>
                                </template>
                            </td>

                        </tr>
                    </tbody>

                    <tbody class="opex">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="budget.OpexCollapsed = !budget.OpexCollapsed">
                                    <i :class="['fa', budget.OpexCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                Opex
                            </th>

                            <th class="text-center">
                                {{fmtNum(budget.OpexCurrentValue)}}
                            </th>

                            <th class="text-center">
                                {{fmtNum(budget.OpexLimit)}}
                            </th>


                        </tr>
                        <tr v-show="budget.OpexCollapsed === false" v-for="p in budget.OpexItems" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>
                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}
                            </td>
                            <td class="text-center">
                                <template v-if="p.Limit != null">
                                    <a class="text-primary" @click="showOpexLimitEditor(p)">{{fmtNum(p.Limit)}}</a>
                                </template>
                                <template v-if="p.Limit == null">
                                    <a class="text-danger font-italic pointer" @click="showOpexLimitEditor(p)">Not set</a>
                                </template>
                            </td>

                        </tr>
                    </tbody>

                    <tbody class="capex">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="budget.CapexCollapsed = !budget.CapexCollapsed">
                                    <i :class="['fa', budget.CapexCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                Capex
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.CapexCurrentValue)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.CapexLimit)}}
                            </th>
                        </tr>
                        <tr v-show="budget.CapexCollapsed === false" v-for="p in budget.CapexItems" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>
                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}
                            </td>

                            <td class="text-center">
                                <template v-if="p.Limit != null">
                                    <a class="text-primary" @click="showCapexLimitEditor(p)">{{fmtNum(p.Limit)}}</a>
                                </template>
                                <template v-if="p.Limit == null">
                                    <a class="text-danger font-italic pointer" @click="showCapexLimitEditor(p)">Not set</a>
                                </template>
                            </td>

                        </tr>
                    </tbody>

                    <tbody class="othex">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="budget.OthexCollapsed = !budget.OthexCollapsed">
                                    <i :class="['fa', budget.OthexCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                Other<br />
                                <small class="text-muted">Non-Opex, Non-COGS</small>
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.OthexCurrentValue)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.OthexLimit)}}
                            </th>


                        </tr>
                        <tr v-show="budget.OthexCollapsed === false" v-for="p in budget.OthexItems" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>
                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}
                            </td>
                            <td class="text-center">
                                <template v-if="p.Limit != null">
                                    <a class="text-primary" @click="showOthexLimitEditor(p)">{{fmtNum(p.Limit)}}</a>
                                </template>
                                <template v-if="p.Limit == null">
                                    <a class="text-danger font-italic pointer" @click="showOthexLimitEditor(p)">Not set</a>
                                </template>
                            </td>
                        </tr>
                    </tbody>

                    <tbody class="expenses">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="budget.ExpensesCollapsed = !budget.ExpensesCollapsed">
                                    <i :class="['fa', budget.ExpensesCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                Expenses (All)<br />
                                <small class="text-muted">Cogs, Opex, Other</small>
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.ExpensesCurrentValue)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.ExpensesLimit)}}
                            </th>
                        </tr>
                        <tr v-show="budget.ExpensesCollapsed === false" v-for="p in budget.ExpensesItems" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>
                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}
                            </td>
                            <td class="text-center">
                                <template v-if="p.Limit != null">
                                    <a class="text-primary" @click="showExpensesLimitEditor(p)">{{fmtNum(p.Limit)}}</a>
                                </template>
                                <template v-if="p.Limit == null">
                                    <a class="text-danger font-italic pointer" @click="showExpensesLimitEditor(p)">Not set</a>
                                </template>
                            </td>
                        </tr>
                    </tbody>

                    <tbody class="opex-expenses">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="budget.OpexExpensesCollapsed = !budget.OpexExpensesCollapsed">
                                    <i :class="['fa', budget.OpexExpensesCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                Expenses (Opex)<br />
                                <small class="text-muted">Opex Expense Accounts</small>
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.OpexExpensesCurrentValue)}}
                            </th>
                            <th class="text-center">
                                {{fmtNum(budget.OpexExpensesLimit)}}
                            </th>
                        </tr>
                        <tr v-show="budget.OpexExpensesCollapsed === false" v-for="p in budget.OpexExpensesItems" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>
                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}
                            </td>
                            <td class="text-center">
                                <template v-if="p.Limit != null">
                                    <a class="text-primary" @click="showOpexExpensesLimitEditor(p)">{{fmtNum(p.Limit)}}</a>
                                </template>
                                <template v-if="p.Limit == null">
                                    <a class="text-danger font-italic pointer" @click="showOpexExpensesLimitEditor(p)">Not set</a>
                                </template>
                            </td>
                        </tr>
                    </tbody>

                </table>

            </div>

            <div class="mt-5">
                <table class="table table-bordered table-hover">
                    <thead>
                        <tr class="table-secondary">
                            <th colspan="2" class="align-middle">Resource allocation</th>
                            <th class="align-middle text-center">
                                Target
                                <br />
                                <small class="text-muted">% of available</small>
                            </th>
                            <th class="align-middle text-center">
                                Capacity <br />
                                <small class="text-muted">Target based</small>
                            </th>
                            <th class="align-middle text-center">Allocation</th>
                        </tr>
                    </thead>
                    <tbody v-for="r in resources" :key="r.Id">
                        <tr>
                            <th class="align-middle text-center" style="width: 60px;">
                                <button class="btn btn-outline-secondary" @click="r.IsCollapsed = !r.IsCollapsed">
                                    <i :class="['fa', r.IsCollapsed ? 'fa-chevron-down' : 'fa-chevron-up']"></i>
                                </button>
                            </th>
                            <th>
                                {{r.Name}}
                            </th>
                            <th class="text-center">
                                <span :class="[r.TargetPercent > 1  ? 'text-danger' : 'text-dark']">{{fmtPct(r.TargetPercent, 0)}}</span>
                            </th>

                            <th class="text-center">
                                <span :class="[r.TargetPercent > 1  ? 'text-danger' : 'text-dark']">{{fmtNum(r.TargetValue)}} {{r.Unit}}</span>
                            </th>

                            <th class="text-center">
                                {{fmtNum(r.CurrentValue)}} {{r.Unit}} <br />
                                <small class="text-muted">{{fmtPct(r.CurrentPercent)}}</small>
                            </th>
                        </tr>
                        <tr v-show="r.IsCollapsed == false" v-for="p in r.Portfolios" :key="p.Id">
                            <td></td>
                            <td>{{p.Name}}</td>

                            <td class="text-center">
                                <template v-if="p.IsLinked === true">
                                    <div class="dropdown">
                                        <a class="text-primary" data-toggle="dropdown">
                                            {{ fmtPct(p.TargetPercent, 0) }}
                                        </a>
                                        <div class="dropdown-menu dropdown-menu-scrollable dropdown-menu-right" role="menu">
                                            <a class="dropdown-item" v-for="n in 41" :key="n" @click="handleSetPortfolioResourceAllocationCommand(p.Id, r.Id, (n-1)/20)">
                                                {{ (n - 1) * 5 }}%
                                            </a>
                                        </div>
                                    </div>
                                </template>
                            </td>
                            <td class="text-center">
                                <template v-if="p.IsLinked === true">
                                    {{fmtNum(p.TargetValue)}} {{r.Unit}}
                                </template>
                            </td>
                            <td class="text-center">
                                {{fmtNum(p.CurrentValue)}}<br />
                                <small class="text-muted">{{fmtPct(p.CurrentPercent)}}</small>
                            </td>
                        </tr>

                    </tbody>


                </table>
            </div>


            <div class="mt-5">
                <table class="table table-bordered table-hover">
                    <thead>
                        <tr class="table-secondary">
                            <th>Capacity minus allocated</th>
                            <th class="text-center">Jan</th>
                            <th class="text-center">Feb</th>
                            <th class="text-center">Mar</th>
                            <th class="text-center">Apr</th>
                            <th class="text-center">May</th>
                            <th class="text-center">Jun</th>
                            <th class="text-center">Jul</th>
                            <th class="text-center">Aug</th>
                            <th class="text-center">Sep</th>
                            <th class="text-center">Oct</th>
                            <th class="text-center">Nov</th>
                            <th class="text-center">Dec</th>
                        </tr>
                    </thead>
                    <tbody v-for="r in rm.Usage" :key="r.Id">
                        <tr>
                            <td>
                                <a class="text-primary" v-sanitizedHref="`/#/enterprise/resource-capacity/${workspaceId}/${strategyId}/${r.Id}`">{{r.Name}} {{r.Unit}}</a>
                            </td>
                            <td colspan="12"></td>
                        </tr>
                        <tr v-for="y in r.Years" :key="y.Year">
                            <td class="align-middle text-center">{{y.Year}}</td>
                            <td class="align-middle text-center" v-for="i in 12" :key="i" :class="{'table-danger': y.Capacity[i-1] - y.Allocation[i-1] < 0}">
                                {{fmtNum(y.Capacity[i-1] - y.Allocation[i-1])}}
                            </td>
                        </tr>

                    </tbody>


                </table>

            </div>
        </div>
    </div>
</template>

<style scoped>
    .toolbar {
        background-color: #f8f8f8; /* Light gray background for the toolbar */
        padding: 10px 15px; /* Padding around the toolbar */
        border: 1px solid #e4e4e4; /* A light border to give separation */
        display: flex; /* Use flex to give layout flexibility */
        align-items: center; /* Center-align items vertically */
        justify-content: space-between; /* Separate items */
    }
</style>
